import type { Folder } from '@/types/email';

/**
 * Resolves the archive folder path for a given account using cached folders.
 * Prefers explicit archive role, but falls back to common archive naming patterns.
 * 
 * Note: Returns the folder path even for Gmail's "All Mail". The backend is responsible
 * for handling Gmail archiving correctly (removing INBOX label instead of adding All Mail label).
 */
export function getArchiveFolderPath(
  accountId: string,
  accountFolderCache: Record<string, Folder[]>
): string | null {
  const accountFolders = accountFolderCache[accountId] ?? [];

  // Prefer folders with explicit 'archive' role
  const archiveFolderByRole = accountFolders.find((folder) => folder?.role === 'archive');
  if (archiveFolderByRole) {
    return archiveFolderByRole.path;
  }

  // Fallback to name/path matching
  const archiveFolder = accountFolders.find((folder) => {
    if (!folder) {
      return false;
    }
    const normalizedName = folder.name?.toLowerCase?.() ?? '';
    const normalizedPath = folder.path?.toLowerCase?.() ?? '';

    return (
      normalizedName === 'archive' ||
      normalizedName === 'archives' ||
      normalizedPath.includes('archive') ||
      normalizedName === 'all mail' ||
      normalizedPath.includes('all mail')
    );
  });

  return archiveFolder ? archiveFolder.path : null;
}

/**
 * Resolves the spam/junk folder path for a given account using cached folders.
 */
export function getSpamFolderPath(
  accountId: string,
  accountFolderCache: Record<string, Folder[]>
): string | null {
  const accountFolders = accountFolderCache[accountId] ?? [];

  const spamFolderByRole = accountFolders.find((folder) => folder?.role === 'spam');
  if (spamFolderByRole) {
    return spamFolderByRole.path;
  }

  const spamFolder = accountFolders.find((folder) => {
    if (!folder) {
      return false;
    }
    const normalizedName = folder.name?.toLowerCase?.() ?? '';
    const normalizedPath = folder.path?.toLowerCase?.() ?? '';
    return (
      normalizedName === 'spam' ||
      normalizedName === 'junk' ||
      normalizedPath.includes('spam') ||
      normalizedPath.includes('junk')
    );
  });

  return spamFolder ? spamFolder.path : null;
}

/**
 * Resolves the trash folder path for a given account using cached folders.
 */
export function getTrashFolderPath(
  accountId: string,
  accountFolderCache: Record<string, Folder[]>
): string | null {
  const accountFolders = accountFolderCache[accountId] ?? [];

  // Prefer folders with explicit 'trash' role
  const trashFolderByRole = accountFolders.find((folder) => folder?.role === 'trash');
  if (trashFolderByRole) {
    return trashFolderByRole.path;
  }

  // Fallback to name/path matching
  const trashFolder = accountFolders.find((folder) => {
    if (!folder) {
      return false;
    }
    const normalizedName = folder.name?.toLowerCase?.() ?? '';
    const normalizedPath = folder.path?.toLowerCase?.() ?? '';

    return (
      normalizedName === 'trash' ||
      normalizedPath.includes('trash') ||
      normalizedName === 'deleted' ||
      normalizedPath.includes('deleted')
    );
  });

  return trashFolder ? trashFolder.path : null;
}
